import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDocument, deleteDocument, getCryptoOriginDocument } from "../../../api/filings/documentsAPI";
import { Handler401 } from "../../../api/utils/responseActions";
import DisplayDocument from "../popup/DisplayDocument";
import ConfirmationModal from "../popup/ConfirmationModal";
import { decodeBase64ToBytes } from "../../../utils/files/decoder";
import { detectFileType } from "../../../utils/files/detectFileType";

function DocumentList({ documents = [], onDocumentDeleted, isCpa = false, disableDelete = false }) {
  const [displayDocument, setDisplayDocument] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(true);
  const [documentFile, setDocumentFile] = useState(null);
  const [documentName, setDocumentName] = useState("");
  const [mimeType, setMimeType] = useState("application/pdf");
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const navigate = useNavigate();

  const fetchDocument = async (documentId) => {
    const apiUrl = "tax/document";
    try {
      const response = await getDocument(apiUrl, documentId);
      if (response.status === 200) {
        const data = await response.json();
        return data.fileEncodedBase64;
      } else {
        if (response.status === 401) {
          Handler401(navigate);
          return null;
        }
        console.error("Failed to fetch document");
        return null;
      }
    } catch (err) {
      console.error("Error fetching document:", err);
      return null;
    }
  };

  const fetchCryptoDocument = async (documentId) => {
    try {
      const response = await getCryptoOriginDocument(documentId);
      if (response.status === 200) {
        const data = await response.json();
        // The crypto endpoint might return fileEncodedBase64 or file_content
        return data.fileEncodedBase64 || data.file_content || data.content;
      } else {
        if (response.status === 401) {
          Handler401(navigate);
          return null;
        }
        console.error("Failed to fetch crypto document");
        return null;
      }
    } catch (err) {
      console.error("Error fetching crypto document:", err);
      return null;
    }
  };

  const handleViewDocument = async (document) => {
    setIsLoadingDocument(true);
    setDisplayDocument(true);
    setDocumentName(document.documentName || "Document");
    
    // Check if this is a crypto document
    const isCryptoDoc = document._isCryptoDocument === true;
    
    const fileEncoded = isCryptoDoc 
      ? await fetchCryptoDocument(document.documentId)
      : await fetchDocument(document.documentId);
      
    if (fileEncoded) {
      const byteArray = decodeBase64ToBytes(fileEncoded);
      const { mimeType: detectedMimeType } = detectFileType(byteArray);
      setMimeType(detectedMimeType || "application/pdf");
      setDocumentFile(fileEncoded);
    }
    setIsLoadingDocument(false);
  };

  const hideShowDocument = () => {
    setDisplayDocument(false);
    setIsLoadingDocument(true);
    setDocumentFile(null);
  };

  const handleDeleteClick = (document, event) => {
    event.stopPropagation();
    setDocumentToDelete(document);
    setShowDeleteConfirmation(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setDocumentToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    setDeletingId(documentToDelete.documentId);
    try {
      const isCryptoDoc = documentToDelete._isCryptoDocument === true;
      const standardizedId = documentToDelete._standardizedId || documentToDelete._cryptoDoc?.standardized_id;

      let apiUrl = "tax/document";
      let deleteId = documentToDelete.documentId;

      if (isCryptoDoc) {
        apiUrl = "crypto/standardized-document";
        if (standardizedId) {
          deleteId = standardizedId;
        } else {
          console.warn("Missing standardized id for crypto document, falling back to origin delete.");
          apiUrl = "crypto/origin-document";
        }
      }

      const response = await deleteDocument(apiUrl, deleteId, { includeMetrics: true });
      
      if (response.status === 204 || response.status === 200) {
        // Try to get summary_metrics from response (200 returns JSON with metrics)
        let summaryMetrics = null;
        if (response.status === 200) {
          try {
            const data = await response.json();
            summaryMetrics = data.summary_metrics;
          } catch (e) {
            // Ignore JSON parse errors
          }
        }
        
        // Call callback to refresh documents list, passing metrics if available
        if (onDocumentDeleted) {
          onDocumentDeleted(summaryMetrics);
        }
        setShowDeleteConfirmation(false);
        setDocumentToDelete(null);
      } else if (response.status === 401) {
        Handler401(navigate);
        setShowDeleteConfirmation(false);
        setDocumentToDelete(null);
      } else {
        const errorText = await response.text().catch(() => "Could not read error response");
        console.error(`Failed to delete document (${response.status}):`, errorText);
        alert("Failed to delete document. Please try again.");
      }
    } catch (err) {
      console.error("Error deleting document:", err);
      alert("An error occurred while deleting the document.");
    } finally {
      setDeletingId(null);
    }
  };

  if (documents.length === 0) {
    return null;
  }

  return (
    <>
      <div className="document-list-container">
        {documents.map((doc) => (
          <div key={doc.documentId} className="document-list-item">
            <button
              type="button"
              onClick={() => handleViewDocument(doc)}
              className="document-list-name"
            >
              {doc.documentName || `Document ${doc.documentId}`}
            </button>
            {!isCpa && !disableDelete && (
              <button
                type="button"
                onClick={(e) => handleDeleteClick(doc, e)}
                disabled={deletingId === doc.documentId}
                className="document-list-delete"
                aria-label="Delete document"
              >
                <img 
                  src={`${process.env.PUBLIC_URL}/images/icons/delete_red.svg`} 
                  alt="Delete" 
                />
              </button>
            )}
          </div>
        ))}
      </div>

      {displayDocument && (
        <DisplayDocument
          documentName={documentName}
          documentFile={documentFile}
          mimeType={mimeType}
          isLoadingDocument={isLoadingDocument}
          hideShowDocument={hideShowDocument}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Document"
        message={`Are you sure you want to delete "${documentToDelete?.documentName || 'this document'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="primary"
        cancelButtonClass="secondary"
        isLoading={deletingId !== null}
      />
    </>
  );
}

export default DocumentList;

